import React, { useState } from 'react';
import { FixedSizeGrid as Grid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { createThumbnailUrl, formatFileSize } from '../utils/imageUtils';

const ThumbnailGallery = ({ photos, onDelete, onEdit, onPhotoClick }) => {
  const [loadedImages, setLoadedImages] = useState(new Set());

  if (!photos || photos.length === 0) {
    return null;
  }

  const handleImageLoad = (photoId) => {
    setLoadedImages(prev => new Set([...prev, photoId]));
  };

  const ThumbnailCell = ({ columnIndex, rowIndex, style, data }) => {
    const { photos, columnCount, onDelete, onEdit, onPhotoClick } = data;
    const index = rowIndex * columnCount + columnIndex;
    
    if (index >= photos.length) {
      return null;
    }

    const photo = photos[index];
    const imageUrl = createThumbnailUrl(photo);
    const isLoaded = loadedImages.has(photo.id);

    return (
      <div style={style} className="p-2">
        <div className="relative group h-full bg-gray-100 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
          {/* Thumbnail Image - Directly Clickable */}
          {!isLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          )}
          <img
            src={imageUrl}
            alt={photo.fileName}
            loading="lazy"
            onLoad={() => handleImageLoad(photo.id)}
            onMouseUp={() => {
              // Using onMouseUp instead of onClick because react-window interferes with onClick
              onPhotoClick(photo, index);
            }}
            className={`w-full h-full object-cover transition-opacity duration-300 cursor-pointer ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ pointerEvents: 'auto' }}
          />

          {/* File Info */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <p className="text-white text-xs truncate">{photo.fileName}</p>
            {photo.size && (
              <p className="text-white text-xs opacity-75">{formatFileSize(photo.size)}</p>
            )}
          </div>

          {/* Edited Badge */}
          {photo.edited && (
            <div className="absolute top-2 left-2 bg-primary-600 text-white text-xs px-2 py-1 rounded-full shadow">
              Edited
            </div>
          )}
        </div>
      </div>
    );
  };

  const getColumnCount = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 640) return 2; // mobile
      if (window.innerWidth < 1024) return 3; // tablet
      return 4; // desktop
    }
    return 3;
  };

  const columnCount = getColumnCount();
  const rowCount = Math.ceil(photos.length / columnCount);
  const cellWidth = window.innerWidth / columnCount;
  const cellHeight = cellWidth; // Square thumbnails

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4 px-4">
        <h2 className="text-xl font-semibold text-gray-800">
          📷 Your Photos ({photos.length})
        </h2>
      </div>

      <div className="w-full" style={{ height: '60vh', minHeight: '400px' }}>
        <AutoSizer>
          {({ height, width }) => {
            const cols = Math.floor(width / 150); // Minimum 150px per thumbnail
            const finalCols = Math.max(2, Math.min(cols, 6)); // 2-6 columns
            const finalCellWidth = width / finalCols;
            const finalRows = Math.ceil(photos.length / finalCols);

            return (
              <Grid
                columnCount={finalCols}
                columnWidth={finalCellWidth}
                height={height}
                rowCount={finalRows}
                rowHeight={finalCellWidth}
                width={width}
                itemData={{
                  photos,
                  columnCount: finalCols,
                  onDelete,
                  onEdit,
                  onPhotoClick,
                }}
                className="scrollbar-thin"
              >
                {ThumbnailCell}
              </Grid>
            );
          }}
        </AutoSizer>
      </div>
    </div>
  );
};

export default ThumbnailGallery;

